/**
 * P429.5 -- the regulator's floor list, and the bank's answer to each item.
 *
 * A floor item is something a regulator pre-admits. Nothing is seeded:
 * the page starts empty and says why, because SAMA's floor is
 * recommendation-driven and a list the product invented would be a
 * supervisor's words nobody wrote. Each item is answered either by
 * admitting it through a service of the same class, or by opting out
 * with a reason that is not in force until the CRO approves it.
 */
import { refusalMessage } from "shared/api/refusalMessage";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api, type ApiError } from "shared/api/client";
import type { FloorItem, FloorList } from "shared/api/types";
import { useMe } from "shared/api/useMe";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { useToast } from "shared/toast/ToastProvider";

const KEY = ["floor-list"] as const;
const P = "modules.foundation.floorList";

interface IbsOption {
  id: string;
  name: string;
  service_class: string;
  admission_status?: string | null;
}

function ItemRow({ item, services }: { item: FloorItem; services: IbsOption[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const me = useMe();
  const [choice, setChoice] = useState<"admitted" | "opted_out">("admitted");
  const [serviceId, setServiceId] = useState("");
  const [reason, setReason] = useState("");
  const done = () => void qc.invalidateQueries({ queryKey: KEY });
  const fail = (err: ApiError) =>
    toast.push({ tone: "error", message: refusalMessage(t, err) });
  const answer = useMutation<FloorItem, ApiError>({
    mutationFn: () =>
      api.put<FloorItem>(`/v1/admin/foundation/floor-items/${item.id}/disposition`, {
        disposition: choice,
        ibs_service_id: choice === "admitted" ? serviceId : null,
        justification: choice === "opted_out" ? reason : null,
      }),
    onSuccess: done,
    onError: fail,
  });
  /* Round 16 O6 -- an item can be taken back. Clearing an answer and
     removing an item are each asked first, and each is recorded; an
     answered item still cannot be removed from under its answer. */
  const [confirming, setConfirming] = useState<"clear" | "remove" | null>(null);
  const clear = useMutation<FloorItem, ApiError>({
    mutationFn: () =>
      api.delete<FloorItem>(`/v1/admin/foundation/floor-items/${item.id}/disposition`),
    onSuccess: () => {
      setConfirming(null);
      toast.push({
        tone: "success",
        message: t(`${P}.cleared`, { label: item.label }),
      });
      done();
    },
    onError: fail,
  });
  const remove = useMutation<void, ApiError>({
    mutationFn: () => api.delete<void>(`/v1/admin/foundation/floor-items/${item.id}`),
    onSuccess: () => {
      setConfirming(null);
      toast.push({
        tone: "success",
        message: t(`${P}.removed`, { label: item.label }),
      });
      done();
    },
    onError: fail,
  });
  const approve = useMutation<FloorItem, ApiError>({
    mutationFn: () =>
      api.post<FloorItem>(
        `/v1/admin/foundation/floor-items/${item.id}/disposition/approve`,
      ),
    onSuccess: done,
    onError: fail,
  });
  const d = item.disposition;
  // Round 16 O6 -- a removed service answers nothing; the server says
  // so too.
  const sameClass = services.filter(
    (s) => s.service_class === item.service_class && s.admission_status !== "removed",
  );
  const canApprove =
    !!me.data &&
    (me.data.is_admin || (me.data.realm_roles ?? []).includes("cro")) &&
    d?.disposition === "opted_out" &&
    !d.in_force;
  const state = !d ? "unanswered" : d.answered ? "answered" : "pending";
  return (
    <li
      data-testid="floor-item"
      data-state={state}
      className="space-y-2 rounded border border-slate-200 bg-white p-3 text-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-900">{item.label}</span>
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
          {t(`${P}.class.${item.service_class}`)}
        </span>
        <span
          className={[
            "rounded-full px-2 py-0.5 text-xs",
            state === "answered"
              ? "bg-emerald-50 text-emerald-800"
              : state === "pending"
                ? "bg-amber-50 text-amber-800"
                : "bg-rose-50 text-rose-800",
          ].join(" ")}
        >
          {t(`${P}.state.${state}`)}
        </span>
      </div>
      <p className="text-xs text-slate-500">{item.source_citation}</p>
      {d && (
        <p data-testid="floor-item-answer" className="text-xs text-slate-700">
          {d.disposition === "admitted"
            ? t(`${P}.admittedThrough`, { service: d.ibs_service_name ?? "" })
            : t(`${P}.optedOut`, { reason: d.justification ?? "" })}{" "}
          {d.disposition === "opted_out" &&
            (d.in_force
              ? t(`${P}.approvedBy`, { who: d.approved_by_email ?? "" })
              : t(`${P}.awaitingApproval`))}
          {d.disposition === "admitted" &&
            d.ibs_admission_status !== "admitted" &&
            ` ${t(`${P}.notYetAdmitted`)}`}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label={t(`${P}.answerLabel`)}
          data-testid="floor-item-choice"
          value={choice}
          onChange={(e) => setChoice(e.target.value as "admitted" | "opted_out")}
          className="rounded border border-slate-300 px-2 py-0.5 text-xs"
        >
          <option value="admitted">{t(`${P}.choice.admitted`)}</option>
          <option value="opted_out">{t(`${P}.choice.opted_out`)}</option>
        </select>
        {choice === "admitted" ? (
          <select
            aria-label={t(`${P}.serviceLabel`)}
            data-testid="floor-item-service"
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
            className="rounded border border-slate-300 px-2 py-0.5 text-xs"
          >
            <option value="">{t(`${P}.pickService`)}</option>
            {sameClass.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        ) : (
          <input
            aria-label={t(`${P}.reasonLabel`)}
            data-testid="floor-item-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="min-w-[16rem] rounded border border-slate-300 px-2 py-0.5 text-xs"
          />
        )}
        <button
          type="button"
          data-testid="floor-item-save"
          disabled={
            answer.isPending || (choice === "admitted" ? !serviceId : !reason.trim())
          }
          onClick={() => answer.mutate()}
          className="rounded bg-brand-brass px-2 py-0.5 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
        >
          {t(`${P}.save`)}
        </button>
        {d && (
          <button
            type="button"
            data-testid="floor-item-clear"
            onClick={() => setConfirming("clear")}
            className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50"
          >
            {t(`${P}.clear`)}
          </button>
        )}
        <button
          type="button"
          data-testid="floor-item-remove"
          disabled={!!d}
          title={d ? t(`${P}.removeAnswered`) : undefined}
          onClick={() => setConfirming("remove")}
          className="rounded border border-rose-200 px-2 py-0.5 text-xs text-rose-800 hover:bg-rose-50 disabled:opacity-50"
        >
          {t(`${P}.remove`)}
        </button>
        {canApprove && (
          <button
            type="button"
            data-testid="floor-item-approve"
            disabled={approve.isPending}
            onClick={() => approve.mutate()}
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-xs font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
          >
            {t(`${P}.approve`)}
          </button>
        )}
      </div>
      {d && <p className="text-xs text-slate-500">{t(`${P}.removeAnswered`)}</p>}
      {confirming && (
        <div
          data-testid="floor-item-confirm"
          role="alertdialog"
          className="flex flex-wrap items-center gap-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900"
        >
          <span>
            {t(confirming === "clear" ? `${P}.confirmClear` : `${P}.confirmRemove`, {
              label: item.label,
            })}
          </span>
          <button
            type="button"
            data-testid="floor-item-confirm-yes"
            disabled={clear.isPending || remove.isPending}
            onClick={() => (confirming === "clear" ? clear.mutate() : remove.mutate())}
            className="rounded bg-rose-700 px-2 py-0.5 font-medium text-white disabled:opacity-50"
          >
            {t(confirming === "clear" ? `${P}.clear` : `${P}.remove`)}
          </button>
          <button
            type="button"
            data-testid="floor-item-confirm-no"
            onClick={() => setConfirming(null)}
            className="rounded border border-slate-300 px-2 py-0.5"
          >
            {t(`${P}.cancel`)}
          </button>
        </div>
      )}
    </li>
  );
}

export function FloorListAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery<FloorList, ApiError>({
    queryKey: KEY,
    queryFn: () => api.get<FloorList>("/v1/admin/foundation/floor-items"),
    retry: false,
  });
  const services = useQuery<IbsOption[], ApiError>({
    queryKey: ["floor-list", "services"],
    queryFn: () => api.get<IbsOption[]>("/v1/admin/foundation/ibses?limit=500"),
    retry: false,
  });
  const [form, setForm] = useState({
    item_key: "",
    label: "",
    service_class: "customer",
    source_citation: "",
  });
  const add = useMutation<FloorItem, ApiError>({
    mutationFn: () => api.post<FloorItem>("/v1/admin/foundation/floor-items", form),
    onSuccess: (created) => {
      // Round 16 O7 -- adding an item gave no confirmation.
      toast.push({
        tone: "success",
        message: t(`${P}.added`, { label: created.label }),
      });
      setForm({
        item_key: "",
        label: "",
        service_class: "customer",
        source_citation: "",
      });
      void qc.invalidateQueries({ queryKey: KEY });
    },
    onError: (err) => toast.push({ tone: "error", message: refusalMessage(t, err) }),
  });
  const items = Array.isArray(list.data?.items) ? list.data.items : [];
  const options = Array.isArray(services.data) ? services.data : [];
  const door = list.data?.admits_internal_functions;
  return (
    <article className="space-y-4" data-testid="floor-list-page">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 font-display text-2xl text-slate-900">
          <MarsadIcon name="obligation" className="text-brand-brass" />
          {t(`${P}.heading`)}
        </h1>
        <p className="text-sm text-slate-600">{t(`${P}.standfirst`)}</p>
        {list.data && (
          <p data-testid="floor-list-door" className="text-xs text-slate-600">
            {t(
              door === true
                ? `${P}.door.open`
                : door === false
                  ? `${P}.door.closed`
                  : `${P}.door.unconfirmed`,
              { regime: list.data.regime_code.toUpperCase() },
            )}
          </p>
        )}
      </header>
      {list.isError ? (
        <p role="alert" className="text-sm text-rose-800">
          {list.error.detail}
        </p>
      ) : !list.data ? null : items.length === 0 ? (
        <p data-testid="floor-list-empty" className="text-sm text-slate-600">
          {t(`${P}.empty`)}
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <ItemRow key={item.id} item={item} services={options} />
          ))}
        </ul>
      )}
      <form
        data-testid="floor-item-form"
        className="grid gap-2 rounded border border-slate-200 bg-white p-3 text-sm sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
      >
        <h2 className="text-sm font-semibold text-slate-900 sm:col-span-2">
          {t(`${P}.addHeading`)}
        </h2>
        <label className="text-xs text-slate-600">
          {t(`${P}.field.label`)}
          <input
            data-testid="floor-form-label"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
        <label className="text-xs text-slate-600">
          {t(`${P}.field.key`)}
          <input
            data-testid="floor-form-key"
            value={form.item_key}
            onChange={(e) => setForm({ ...form, item_key: e.target.value })}
            className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
        <label className="text-xs text-slate-600">
          {t(`${P}.field.class`)}
          <select
            data-testid="floor-form-class"
            value={form.service_class}
            onChange={(e) => setForm({ ...form, service_class: e.target.value })}
            className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
          >
            {(["customer", "state", "internal"] as const).map((c) => (
              <option key={c} value={c}>
                {t(`${P}.class.${c}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          {t(`${P}.field.citation`)}
          <input
            data-testid="floor-form-citation"
            value={form.source_citation}
            onChange={(e) => setForm({ ...form, source_citation: e.target.value })}
            className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
        <div className="sm:col-span-2">
          <button
            type="submit"
            data-testid="floor-form-add"
            disabled={
              add.isPending ||
              !form.label.trim() ||
              !form.item_key.trim() ||
              !form.source_citation.trim()
            }
            className="rounded bg-brand-brass px-3 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
          >
            {t(`${P}.add`)}
          </button>
        </div>
      </form>
    </article>
  );
}
